import { useEffect, useState } from 'react';
import isEqual from 'lodash.isequal';
import { useForm, UseFormSetValue } from 'react-hook-form';
import {
  ajvResolver,
  ResponseError,
  responseErrorsResolver,
  usePrevious,
} from '@ui/src';
import { useAppDispatch } from '@store';
import {
  useAssignCompanyOfficialMutation,
  useCreateCompanyOfficialMutation,
  useUpdateCorporateQuestionnarieMutation,
} from '../../../../hoc/new-application/store/services';
import {
  AccessRight,
  ApplicationType,
  CompanyOfficialFormValues,
  CorporateFormValues,
  NewApplicationChildrenInfo,
  NewApplicationListItem,
  NewApplicationStatus,
} from '../../../../hoc/new-application/types';
import getCorporateAppicationBody from '../../../../hoc/new-application/corporate/helpers/get-corporate-application-body';
import { CorporateQuestionnarieFormValues } from '../../../../hoc/corporate/types';
import { ApplicationScheme } from '../../../../hoc/common/types';
import getCorporateQuestionnarieBody from '../../../../hoc/new-application/corporate/helpers/get-questionnarie-body';
import {
  updateCompanyOfficialValues,
  updateMainRepresentativeValues,
} from '../../../../hoc/new-application/corporate/store/slices';
import useGetAutocompleteList from '../../../../hoc/new-application/helpers/use-autocomplete-list';

const schema = {
  type: 'object',
  properties: {
    firstName: {
      type: 'string',
      minLength: 1,
      errorMessage: 'First Name is required',
    },
    lastName: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Last Name is required',
    },
    phone: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Phone is required',
    },
    email: {
      type: 'string',
      minLength: 1,
      errorMessage: 'E-mail is required',
    },
    dob: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Date of Birth is required',
    },
    signatureType: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Signature type is required',
    },
    accessRight: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Access Right type is required',
    },
    nationalityId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Nationality is required',
    },
  },
  required: [
    'firstName',
    'lastName',
    'phone',
    'dob',
    'signatureType',
    'accessRight',
    'email',
    'nationalityId',
  ],
};

const cofDefaultFormValues = {
  accessRight: AccessRight.VIEWER,
  applicantId: '',
  deliveryChannelId: '',
  dob: '',
  email: '',
  firstName: '',
  isUSResident: false,
  lastName: '',
  nationalityId: '',
  participatesInPolitics: false,
  phone: '',
  signatureType: '',
  applicationSchemeId: '',
};

const useCompanyOfficial = ({
  applications,
  isCof,
  applicationId,
  fields,
  initialFields,
  onApplicationSubmit,
  questionnarieFields,
  defaultQuestionnarieFormValues,
  applicationScheme,
  onSaveAccountsCurrencies,
  onSaveAccountsFlows,
  onSaveExpectedFundsOrigins,
  applicationsList,
  setValue,
  setDefaultFormValues,
  defaultApplicationFormValues,
}: {
  applications?: NewApplicationChildrenInfo[];
  isCof?: boolean;
  applicationId?: string;
  fields?: CorporateFormValues;
  initialFields?: CorporateFormValues;
  onApplicationSubmit: ({
    id,
    body,
  }: {
    id: string;
    body: CorporateFormValues;
  }) => void;
  questionnarieFields?: CorporateQuestionnarieFormValues;
  defaultQuestionnarieFormValues?: CorporateQuestionnarieFormValues;
  applicationScheme?: ApplicationScheme;
  onSaveAccountsCurrencies: () => void;
  onSaveAccountsFlows: () => void;
  onSaveExpectedFundsOrigins: () => void;
  applicationsList?: NewApplicationChildrenInfo[];
  setValue: UseFormSetValue<CorporateFormValues>;
  setDefaultFormValues: (data: CorporateFormValues) => void;
  defaultApplicationFormValues?: CorporateFormValues;
}) => {
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [clearState, setClearState] = useState(false);
  const [addErrorVisible, setAddErrorVisible] = useState(false);
  const [sumSubError, setSumSubError] = useState('');
  const [sidepanelError, setSidepanelError] = useState('');
  const [list, setList] = useState<
    {
      id: string;
      position: string;
      percentage: string;
      natureOfControl: string;
      signatureType: string;
      status?: NewApplicationStatus;
      accessRight: AccessRight;
    }[]
  >([]);

  const dispatch = useAppDispatch();

  const {
    control: createControl,
    handleSubmit,
    formState: { errors: createErrors, isDirty },
    setError,
    reset,
  } = useForm<CompanyOfficialFormValues>({
    defaultValues: cofDefaultFormValues,
    resolver: ajvResolver(schema),
  });

  const [create, { isLoading, isSuccess }] = useCreateCompanyOfficialMutation();
  const [assign, { isLoading: assignInProgress }] =
    useAssignCompanyOfficialMutation();

  const [updateCorporateQuestionnarie] =
    useUpdateCorporateQuestionnarieMutation();

  const prevApplications = usePrevious(applications);

  const assignCompanyOfficial = async (id: string) => {
    const body = {
      companyOfficialApplicationId: id,
      companyOfficialType: isCof
        ? ApplicationType.COMPANY_OFFICIAL
        : ApplicationType.MAIN_REPRESENTATIVE,
    };

    const response = (await assign({
      id: applicationId as string,
      body,
    })) as {
      data?: { data: NewApplicationChildrenInfo };
      error?: ResponseError;
    };

    const nextFields = { ...fields };

    delete nextFields?.mainRepresentative;
    delete nextFields?.companyOfficial;

    if (!isEqual(nextFields, initialFields)) {
      const applictionBody = getCorporateAppicationBody(nextFields);

      if (!applictionBody.websiteLink) {
        applictionBody.websiteLink = '';
      }

      onApplicationSubmit({
        body: applictionBody,
        id: applicationId as string,
      });
    }

    if (!isEqual(questionnarieFields, defaultQuestionnarieFormValues)) {
      if (
        applicationScheme &&
        (applicationScheme?.accountantDetailsActive ||
          applicationScheme?.auditedByExternalComplianceActive ||
          applicationScheme?.changesInManagementOrOwnershipActive ||
          applicationScheme?.complianceConsultantDetailsActive ||
          applicationScheme?.fcaNumberActive ||
          applicationScheme?.hmrcMlrNumberActive ||
          applicationScheme?.hmrcRegistrationExpiryDateActive ||
          applicationScheme?.icoNumberActive ||
          applicationScheme?.itProviderDetailsActive ||
          applicationScheme?.mlroDetailsActive ||
          applicationScheme?.numberOfAgentsActive ||
          applicationScheme?.numberOfBranchesActive ||
          applicationScheme?.onboardCorporateClientsActive ||
          applicationScheme?.onboardMsbActive ||
          applicationScheme?.paymentsForCharitiesActive ||
          applicationScheme?.payoutPartnerDetailsActive ||
          applicationScheme?.regulatoryInvestigationActive ||
          applicationScheme?.screeningActive ||
          applicationScheme?.transactionMonitoringActive ||
          applicationScheme?.typeOfBusinessLicenceActive ||
          applicationScheme?.visitedByAuthoritiesActive ||
          applicationScheme?.workingWithAnyOtherIPSPActive ||
          applicationScheme?.id === 'cdbb8c71-90e6-4a5e-964f-e55169e0c4df')
      ) {
        const questionnarieBody = getCorporateQuestionnarieBody({
          questionnarieValues:
            questionnarieFields as CorporateQuestionnarieFormValues,
          scheme: applicationScheme,
        });

        updateCorporateQuestionnarie({
          id: applicationId as string,
          body: questionnarieBody,
        });

        if (applicationScheme?.id === 'cdbb8c71-90e6-4a5e-964f-e55169e0c4df') {
          onSaveAccountsCurrencies();
          onSaveAccountsFlows();
          onSaveExpectedFundsOrigins();
        }
      }
    }

    if (response.data && response.data.data && applicationsList?.length) {
      if (
        applicationsList.find((item) => item.id === response?.data?.data.id)
      ) {
        const filteredApplicationsList = applicationsList.filter(
          (item) => item.id !== response?.data?.data.id,
        );

        setValue(
          isCof ? 'mainRepresentative' : 'companyOfficial',
          filteredApplicationsList,
        );

        if (isCof) {
          setDefaultFormValues({
            ...defaultApplicationFormValues,
            mainRepresentative: filteredApplicationsList,
          });
        } else {
          setDefaultFormValues({
            ...defaultApplicationFormValues,
            companyOfficial: filteredApplicationsList,
          });
        }

        if (isCof) {
          dispatch(updateMainRepresentativeValues(filteredApplicationsList));
        } else {
          dispatch(updateCompanyOfficialValues(filteredApplicationsList));
        }
      }
    }

    if (response.data || response.error) {
      setClearState(true);

      setTimeout(() => {
        setClearState(false);
        reset({
          accessRight: AccessRight.VIEWER,
          applicantId: '',
          deliveryChannelId: '',
          dob: '',
          email: '',
          firstName: '',
          isUSResident: false,
          lastName: '',
          nationalityId: '',
          participatesInPolitics: false,
          phone: '',
          signatureType: '',
          applicationSchemeId: '',
        });
        setSumSubError('');
        setSidepanelError('');
      }, 100);
    }
  };

  const {
    autocompleteList,
    onSearch,
    searchQuery,
    isLoading: autocompleteIsLoading,
    setSearchQuery,
    getAutocompleteListAction,
  } = useGetAutocompleteList();

  const onSubmit = handleSubmit(async (values) => {
    setSumSubError('');
    setSidepanelError('');

    const nextFields = { ...fields };

    delete nextFields?.mainRepresentative;
    delete nextFields?.companyOfficial;

    if (!isEqual(nextFields, initialFields)) {
      const applictionBody = getCorporateAppicationBody(nextFields);

      if (!applictionBody.websiteLink) {
        applictionBody.websiteLink = '';
      }

      onApplicationSubmit({
        body: applictionBody,
        id: applicationId as string,
      });
    }

    const dob = new Date(values.dob as string);

    const body: CompanyOfficialFormValues = {
      firstName: values.firstName,
      lastName: values.lastName,
      phone: values.phone,
      email: values?.email,
      dob: `${dob.getFullYear()}-${(dob.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${dob.getDate().toString().padStart(2, '0')}`,
      nationalityId: values.nationalityId,
      deliveryChannelId: values.deliveryChannelId,
      isUSResident: values.isUSResident,
      participatesInPolitics: values.participatesInPolitics,
    };

    if (values.applicantId) {
      body.applicantId = values.applicantId;
    }

    if (values?.applicationSchemeId) {
      body.applicationSchemeId = values.applicationSchemeId;
    }

    const response = (await create({
      id: applicationId as string,
      body,
    })) as {
      data?: { data: NewApplicationListItem };
      error?: ResponseError;
    };

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError: setSidepanelError,
      });

      if (response.error.data && response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0] &&
          typeof response.error.data.message[0] === 'string' &&
          (response.error.data.message[0] as string).includes('applicant')
        ) {
          setSumSubError(response.error.data.message[0]);
        }
      }
    } else {
      reset({
        accessRight: AccessRight.VIEWER,
        applicantId: '',
        deliveryChannelId: '',
        dob: '',
        email: '',
        firstName: '',
        isUSResident: false,
        lastName: '',
        nationalityId: '',
        participatesInPolitics: false,
        phone: '',
        signatureType: '',
        applicationSchemeId: '',
      });

      const assignBody = {
        companyOfficialApplicationId: response?.data?.data?.id as string,
        companyOfficialType: isCof
          ? ApplicationType.COMPANY_OFFICIAL
          : ApplicationType.MAIN_REPRESENTATIVE,
        signatureType: values.signatureType,
        accessRight: values.accessRight || AccessRight.VIEWER,
      };

      const assignResponse = (await assign({
        id: applicationId as string,
        body: assignBody,
      })) as {
        data?: { data?: NewApplicationChildrenInfo };
        error?: ResponseError;
      };

      if (!assignResponse.error && panelIsOpen) {
        setPanelIsOpen(false);
      }
    }
  });

  useEffect(() => {
    if (applications?.length) {
      setList(
        applications.map((item) => ({
          id: item.subApplicationId,
          position: item.position,
          natureOfControl: item.natureOfControl,
          percentage: item.percentage,
          status: item.subApplication.status,
          signatureType: item.signatureType || '',
          accessRight: item.accessRight || AccessRight.NONE,
        })),
      );
    }
  }, [applications]);

  useEffect(() => {
    if (!isEqual(applications, prevApplications)) {
      setList(
        applications?.map((item) => ({
          id: item.subApplicationId,
          position: item.position,
          natureOfControl: item.natureOfControl,
          percentage: item.percentage,
          status: item.subApplication.status,
          signatureType: item.signatureType || '',
          accessRight: item.accessRight || AccessRight.NONE,
        })) || [],
      );
    }
  }, [applications, prevApplications]);

  useEffect(() => {
    if (isSuccess) {
      setPanelIsOpen(false);
      reset({
        accessRight: AccessRight.VIEWER,
        applicantId: '',
        deliveryChannelId: '',
        dob: '',
        email: '',
        firstName: '',
        isUSResident: false,
        lastName: '',
        nationalityId: '',
        participatesInPolitics: false,
        phone: '',
        signatureType: '',
        applicationSchemeId: '',
      });
    }
  }, [isSuccess]);

  return {
    list,
    setList,
    addErrorVisible,
    assignInProgress,
    autocompleteList,
    setPanelIsOpen,
    onSearch,
    autocompleteIsLoading,
    setAddErrorVisible,
    assignCompanyOfficial,
    searchQuery,
    setSearchQuery,
    getAutocompleteListAction,
    clearState,
    panelIsOpen,
    sidepanelError,
    isDirty,
    setDirtyPopupVisible,
    setSidepanelError,
    setSumSubError,
    reset,
    isLoading,
    onSubmit,
    createControl,
    createErrors,
    sumSubError,
    dirtyPopupVisible,
  };
};

export default useCompanyOfficial;
